/**
 * Reports > Gift Cards (/reports/gift-cards).
 *
 * Gift card purchases and redemptions by fiscal month, the balance still on cards, and
 * the balance by month sold and by selling store, with a fiscal month range and an Excel
 * download. Replaces the QuickSight dashboard "Giftcard Reporting".
 *
 * Two figures carry over from QuickSight unchanged and are marked on the screen as
 * pending Finance confirmation: the 0.279950 breakage factor applied to redemptions, and
 * the 2020-03-04 cut-off of the "balance on cards sold before" tile.
 *
 * Thin page: the screen is the shared `GiftCards` component in `@ampersandbrands/ui`
 * (presentational); this file fetches through `useGiftCards`, keeps the fiscal month
 * range in the URL, and adds the page chrome.
 *
 * RBAC: head office only, `reports.giftcards`. No store-manager view.
 *
 * Resilience: wrapped in an ErrorBoundary and degrades to a quiet "not available yet"
 * card on 404/503 while the api ships.
 */

import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { GiftCards as GiftCardsScreen, type GiftRange } from '@ampersandbrands/ui';

import { ErrorBoundary } from '@/components/ErrorBoundary';
import { ToolPinToggle } from '@/components/ToolPinToggle';
import { useSession } from '@/lib/useSession';
import { useReportPageState } from '@/lib/reportPageState';
import { isNotAvailable } from '@/lib/useStores';
import { buildFinanceQs, downloadFinanceXlsx, parseRange, useGiftCards, writeRange } from '@/lib/useFinance';
import { hasAccessSlug } from '@/lib/toolVisibility';
import { AccessDeniedCard, ErrorCard, NotAvailableCard, StoresHeader } from '../stores/storesShared';

/** The accessSlug that grants this report. Head office only. */
const SLUG = 'reports.giftcards';

function GiftCardsInner() {
  const { user } = useSession();
  const canView = hasAccessSlug(user?.permissions, SLUG, user?.superAdmin);

  const [searchParams, setSearchParams] = useSearchParams();
  const urlRange = useMemo(() => parseRange(searchParams), [searchParams]);
  const [exporting, setExporting] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const setRange = useCallback(
    (next: GiftRange | null) =>
      setSearchParams(
        (prev) => {
          const sp = new URLSearchParams(prev);
          writeRange(sp, next);
          return sp;
        },
        { replace: true },
      ),
    [setSearchParams],
  );

  const q = useGiftCards(urlRange, canView);
  const data = q.data;
  const shown = urlRange ?? data?.range ?? null;

  const pageState = useMemo(
    () => [{ label: 'Fiscal months', value: shown ? `${shown.from} to ${shown.to}` : 'Last 12 complete fiscal months' }],
    [shown],
  );
  useReportPageState(pageState);

  async function handleExport() {
    setExporting(true);
    setToast(null);
    try {
      await downloadFinanceXlsx('gift-cards/export', buildFinanceQs(urlRange), 'gift-cards.xlsx');
    } catch (e) {
      setToast(e instanceof Error ? e.message : 'Download failed');
    } finally {
      setExporting(false);
    }
  }

  if (user && !canView) return <AccessDeniedCard what="Gift Cards" />;

  const header = (
    <StoresHeader
      eyebrow="Reports"
      title="Gift Cards"
      subtitle="Gift card purchases and redemptions by fiscal month, and the balance still on cards. Redemptions are negative amounts. Refreshed from CounterPoint once a day."
      actions={<ToolPinToggle toolSlug="gift-cards" toolName="Gift Cards" />}
    />
  );

  if (isNotAvailable(q.error)) {
    return (
      <div className="flex h-full min-h-0 flex-col">
        {header}
        <NotAvailableCard what="Gift Cards" />
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      {header}
      {toast && (
        <p role="alert" className="mb-3 text-sm text-red-700 dark:text-red-300">
          {toast}
        </p>
      )}
      <GiftCardsScreen
        periods={data?.periods ?? []}
        range={shown}
        onRangeChange={setRange}
        kpis={data?.kpis ?? null}
        months={data?.months ?? []}
        totals={data?.totals ?? null}
        byMonthSold={data?.by_month_sold ?? []}
        byStore={data?.by_store ?? []}
        breakageFactor={data?.breakage_factor ?? '0.279950'}
        cutoffDate={data?.cutoff_date ?? '2020-03-04'}
        asOf={data?.as_of ?? null}
        loading={q.isLoading}
        error={q.error ? <ErrorCard message={q.error.message} /> : undefined}
        onExport={() => void handleExport()}
        exporting={exporting}
      />
    </div>
  );
}

export function GiftCards() {
  return (
    <ErrorBoundary>
      <GiftCardsInner />
    </ErrorBoundary>
  );
}
